import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChevronDown, Search, Check } from 'lucide-react';
import HelpHint from '../components/HelpHint';
import { useAuth } from '../contexts/AuthContext';
import { AssetAssignments } from '../components/AssetAssignments';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EDIT_DESCARTE_MOTIVOS = [
  { v: 'FUERA_ZONA', l: 'Fuera de zona operativa' },
  { v: 'POCA_LIQUIDEZ', l: 'Ubicación con poca liquidez' },
  { v: 'DESCARTADO_PREVIAMENTE', l: 'Descartado previamente' },
  { v: 'NO_INMUEBLE_LOCAL', l: 'No inmueble / local' },
  { v: 'NO_RENTABLE', l: 'No rentable' },
  { v: 'ACTIVO_DESACTUALIZADO', l: 'Activo desactualizado (revisar estado judicial)' },
  { v: 'ACTIVO_QUEMADO', l: 'Activo quemado / ya trabajado' },
  { v: 'INFO_INSUFICIENTE', l: 'Información insuficiente' },
  { v: 'OCUPACION_CRITICA', l: 'Problema de ocupación crítico' },
  { v: 'ADQUIRIDA_POR_TERCERO', l: 'Adquirida por tercero' },
];

// (Feb 2026) Picker por chips para asignar/desasignar usuarios en un activo.
// Sustituye al `<select multiple>` nativo (Ctrl+click poco descubrible). Cada
// asignado aparece como chip con X para eliminarlo, y hay un dropdown con
// buscador para añadir nuevos. UX (fix Feb 2026):
//  - El dropdown lista TODOS los usuarios coincidentes, incluidos los ya
//    asignados (marcados con ✓ y click alterna quitar). Antes se filtraban
//    fuera, así el usuario buscaba "med" y no encontraba a `medtest` porque
//    ya estaba en la lista → parecía que el buscador estaba roto.
//  - Input y dropdown con contraste alto para dark theme.
const UserChipsPicker = ({ value = [], options = [], onChange, disabled = false, placeholder = 'Añadir usuario…', testId }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selected = useMemo(() => (
    value.map(uid => options.find(u => u.id === uid) || { id: uid, name: uid.slice(0, 8), email: '', role: '' })
  ), [value, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const toggleUser = (uid) => {
    if (disabled) return;
    if (value.includes(uid)) onChange(value.filter(x => x !== uid));
    else onChange([...value, uid]);
  };
  const removeUser = (uid) => {
    if (disabled) return;
    onChange(value.filter(x => x !== uid));
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      <div
        className={`flex flex-wrap items-center gap-1 min-h-[38px] px-2 py-1 rounded-md border ${disabled ? 'input-base opacity-60 cursor-not-allowed' : 'input-base cursor-text focus-within:border-blue-500'}`}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.length === 0 && !open && (
          <span className="text-sm text-slate-400 px-1">{placeholder}</span>
        )}
        {selected.map(u => (
          <span
            key={u.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-200 border border-blue-500/40"
            data-testid={`${testId}-chip-${u.id}`}
          >
            <span className="truncate max-w-[180px]">{u.name || u.email}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeUser(u.id); }}
                className="p-0.5 rounded-full hover:bg-blue-400/30 transition-colors"
                title={`Quitar a ${u.name || u.email}`}
                data-testid={`${testId}-remove-${u.id}`}
                aria-label={`Quitar ${u.name || u.email}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
            className="ml-auto p-1 rounded hover:bg-slate-700/40 text-slate-400"
            aria-label="Abrir buscador"
            data-testid={`${testId}-toggle`}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full rounded-md shadow-xl max-h-64 overflow-hidden flex flex-col bg-slate-800 border border-slate-600">
          <div className="relative p-2 border-b border-slate-700">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              autoFocus
              value={query}
              // (Feb 2026) `onInput` además de `onChange` garantiza que
              // cada tecla actualice el estado inmediatamente. Algunos
              // navegadores (autofill de Chrome, teclados con IME español
              // usando acentos como dead-keys) pueden reprimir el evento
              // `change` de React hasta blur/Tab; `onInput` cubre ese hueco.
              onChange={e => setQuery(e.target.value)}
              onInput={e => setQuery(e.currentTarget.value)}
              onClick={(e) => e.stopPropagation()}
              // Deshabilitar autocomplete del navegador: sin esto Chrome
              // inyecta un dropdown propio que "come" las pulsaciones y
              // el usuario cree que el buscador no reacciona.
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name={`user-picker-${testId}`}
              placeholder="Buscar por nombre, email o rol"
              className="w-full pl-8 pr-2 py-1.5 text-sm rounded bg-slate-900/60 text-slate-100 placeholder-slate-400 border border-slate-600 focus:outline-none focus:border-blue-500"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-slate-400 text-center">Ningún usuario coincide con "{query}"</p>
            ) : filtered.map(u => {
              const isSelected = value.includes(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggleUser(u.id)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-2 transition-colors ${isSelected ? 'bg-blue-500/10 text-blue-100 hover:bg-blue-500/20' : 'text-slate-100 hover:bg-slate-700/60'}`}
                  data-testid={`${testId}-option-${u.id}`}
                  aria-pressed={isSelected}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-slate-500'}`}>
                      {isSelected && <Check className="w-3 h-3" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{u.name || u.email}</span>
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide flex-shrink-0">{u.role}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EditAssetPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // (Feb 2026) Solo admin/superadmin ven la sección Gestores/Mediadores/
  // Inversores. Un gestor puede editar la ficha pero no reasignar personas.
  const isAdminOrSuper = ['admin', 'superadmin'].includes((user?.role || '').toLowerCase());
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gastos, setGastos] = useState([]);
  const [newGasto, setNewGasto] = useState({ concepto: '', importe: '', categoria: 'otros' });
  const [users, setUsers] = useState([]);
  const [showDescarte, setShowDescarte] = useState(false);
  const [descarteDraft, setDescarteDraft] = useState([]);
  // Checkbox manual "En descuento" (spec Feb 2026).
  const [manualDiscount, setManualDiscount] = useState(false);
  const [originalPrecioCompra, setOriginalPrecioCompra] = useState(null);
  // Checkbox "Pendiente de mediador" — al guardar cambia a fase Mediación
  // y marca el activo como Solicitada Revisión para los admins asignados.
  const [pendienteMediador, setPendienteMediador] = useState(false);
  // Snapshot inicial de estado + etapa. Si el usuario los cambia en el
  // formulario, el checkbox "En descuento" se desmarca automáticamente
  // (paralelo al auto-clear del backend, spec Feb 2026 §3).
  const [originalEstadoEtapa, setOriginalEstadoEtapa] = useState({ estado: null, etapa_visual: null });

  useEffect(() => {
    const load = async () => {
      try {
        const [assetRes, gastosRes, usersRes] = await Promise.all([
          axios.get(`${API}/assets/${id}`, { withCredentials: true }),
          axios.get(`${API}/assets/${id}/gastos`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setForm(assetRes.data);
        // Snapshot del precio inicial para detectar la bajada manual al guardar.
        setOriginalPrecioCompra(assetRes.data?.precio_compra ?? null);
        setOriginalEstadoEtapa({
          estado: assetRes.data?.estado ?? null,
          etapa_visual: assetRes.data?.etapa_visual ?? null,
        });
        setGastos(gastosRes.data.items ?? gastosRes.data);
        setUsers(usersRes.data.items ?? usersRes.data);
      } catch {
        toast.error('Error al cargar');
        navigate('/activos');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  // Auto-desmarca "En descuento" si el usuario cambia estado o etapa desde
  // el formulario (spec Feb 2026 §3). Paralelo al backend, que limpia los
  // campos del badge cuando cambia cualquiera de los dos.
  useEffect(() => {
    if (!manualDiscount || !form) return;
    if (form.estado !== originalEstadoEtapa.estado
        || form.etapa_visual !== originalEstadoEtapa.etapa_visual) {
      setManualDiscount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.estado, form?.etapa_visual]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setNum = (field, raw) => set(field, raw === '' ? null : parseFloat(raw));
  // Edición manual de coordenadas: marca la fuente como manual para no confundirla con Catastro.
  const setCoord = (field, raw) => setForm(prev => {
    const next = { ...prev, [field]: raw === '' ? null : parseFloat(raw), coordenadas_fuente: 'manual', coordenadas_srs: 'EPSG:4326' };
    next.coordenadas_status = next.latitud != null && next.longitud != null ? 'ok' : null;
    return next;
  });

  const handleEstadoChange = (value) => {
    // (Feb 2026) Al pasar a CERRADO, abrir el modal de "Motivo de cierre"
    // (reutiliza el pipeline anterior de `descarte_motivos`).
    if (value === 'cerrado') {
      setDescarteDraft(form?.descarte_motivos || []);
      setShowDescarte(true);
      return;
    }
    // Cualquier otro estado limpia los motivos.
    setForm(prev => ({ ...prev, estado: value, descarte_motivos: [] }));
  };

  const toggleDescarteDraft = (v) => {
    // Single motivo principal: replace the whole array with this value.
    setDescarteDraft([v]);
  };

  const confirmDescarte = () => {
    if (!descarteDraft.length) {
      toast.error('Selecciona al menos un motivo');
      return;
    }
    setForm(prev => ({ ...prev, estado: 'cerrado', descarte_motivos: descarteDraft }));
    setShowDescarte(false);
  };

  const handleSave = async () => {
    // Validación fase Mediación (spec Feb 2026): se requiere asignar al
    // menos un mediador O marcar "Pendiente de mediador" antes de pasar
    // el activo a fase Mediación.
    if (form?.etapa_visual === 'mediacion'
        && (!form.mediadores || form.mediadores.length === 0)
        && !pendienteMediador) {
      toast.error('Asigna un mediador o marca "Pendiente de mediador" antes de pasar a Mediación');
      return;
    }
    setSaving(true);
    try {
      // Strip temporary frontend ids for newly added deudas so the backend assigns a real UUID.
      const payload = { ...form };
      if (!isAdminOrSuper) {
        ['gestores', 'mediadores', 'inversores', 'asignado_a', 'asignado_a_id'].forEach(field => delete payload[field]);
      }
      if (Array.isArray(form.deudas)) {
        payload.deudas = form.deudas.map(d => {
          const { id: did, ...rest } = d;
          return (did && String(did).startsWith('new-')) ? rest : d;
        });
      }
      if (Array.isArray(form.participantes)) {
        payload.participantes = form.participantes.map(p => {
          const { id: pid, ...rest } = p;
          return (pid && String(pid).startsWith('new-')) ? rest : p;
        });
      }
      await axios.put(`${API}/assets/${id}`, payload, { withCredentials: true });
      // Marcado manual "En descuento" — sólo se dispara si el usuario tildó
      // el checkbox y el precio ha bajado respecto al valor original al abrir
      // la ficha (validación repetida en backend con 400 en caso contrario).
      if (manualDiscount) {
        const prev = Number(originalPrecioCompra);
        const curr = Number(form.precio_compra);
        if (!prev || !curr || curr >= prev) {
          toast.error('No se puede marcar en descuento: el precio nuevo debe ser inferior al anterior');
        } else {
          try {
            await axios.post(`${API}/assets/${id}/mark-manual-discount`,
              { prev_price: prev, new_price: curr, field: 'precio_compra' },
              { withCredentials: true });
            toast.success(`Marcado en descuento (${Math.round((curr - prev) / prev * 100)}%)`);
          } catch (e2) {
            toast.error('Error al marcar descuento: ' + (e2.response?.data?.detail || e2.message));
          }
        }
      }
      if (form.tipo_activo_operativo) {
        await axios.post(`${API}/assets/${id}/calculate-roi-advanced`, {
          tipo_activo_operativo: form.tipo_activo_operativo,
          precio_compra: form.precio_compra || 0, deuda_total: form.deuda_total || 0,
          valor_adjudicacion: form.valor_adjudicacion || 0, valor_referencia: form.valor_referencia || 0,
          valor_salida_base: form.valor_salida_base || 0, tiempo_resolucion_meses: form.tiempo_resolucion_meses || 12,
          tipo_impositivo_itp: form.tipo_impositivo_itp || 0, tipo_impositivo_ajd: form.tipo_impositivo_ajd || 0,
          tipo_impositivo_iva: form.tipo_impositivo_iva || 0, vivienda_habitual: form.vivienda_habitual || null,
          tasacion: form.tasacion || 0, coste_posesion: form.coste_posesion || 0,
          fecha_compra: form.fecha_compra || null, fecha_venta: form.fecha_venta || null,
        }, { withCredentials: true }).catch(() => {});
      }
      toast.success('Activo guardado');
      // Si el usuario marcó "Pendiente de mediador" y la fase quedó en
      // "mediacion", notificamos a los admins asignados con la nota
      // automática (spec Feb 2026). No aborta el navigate si falla.
      if (pendienteMediador && form?.etapa_visual === 'mediacion') {
        try {
          const recRes = await axios.get(`${API}/assets/${id}/review-recipients`, { withCredentials: true });
          const admins = ((recRes.data?.groups || []).find(g => g.role === 'admin')?.users || []).map(u => u.id);
          await axios.post(`${API}/assets/${id}/toggle-review`,
            { marked: true, recipient_ids: admins, note: 'Mediador pendiente de asignar / revisar (auto)' },
            { withCredentials: true });
          toast.success('Marcado como pendiente de revisión');
        } catch (e) {
          toast.error('No se pudo marcar pendiente de revisión: ' + (e.response?.data?.detail || e.message));
        }
      }
      navigate(`/activos/${id}`);
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const addGasto = async () => {
    if (!newGasto.concepto || !newGasto.importe) return;
    try {
      await axios.post(`${API}/assets/${id}/gastos`, { asset_id: id, concepto: newGasto.concepto, importe: parseFloat(newGasto.importe) || 0, categoria: newGasto.categoria }, { withCredentials: true });
      setNewGasto({ concepto: '', importe: '', categoria: 'otros' });
      const res = await axios.get(`${API}/assets/${id}/gastos`, { withCredentials: true });
      setGastos(res.data.items ?? res.data);
    } catch { toast.error('Error al añadir gasto'); }
  };

  const deleteGasto = async (gid) => {
    try {
      await axios.delete(`${API}/assets/${id}/gastos/${gid}`, { withCredentials: true });
      setGastos(prev => prev.filter(g => g.id !== gid));
    } catch { toast.error('Error al eliminar'); }
  };

  if (loading || !form) return <div className="flex items-center justify-center h-64"><div className="text-blue-600">Cargando...</div></div>;

  const gastosTotal = gastos.reduce((s, g) => s + g.importe, 0);

  return (
    <div className="max-w-4xl mx-auto" data-testid="edit-asset-page">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(`/activos/${id}`)} className="text-slate-500 hover:text-slate-700 text-2xl" data-testid="back-btn">&larr;</button>
          <div>
            <h1 className="heading-1">Editar Activo</h1>
            <p className="body-text">{form.codigo_npl ? <span className="inline-block bg-slate-900 text-white text-xs font-semibold px-2 py-0.5 rounded mr-2">{form.codigo_npl}</span> : null}{form.direccion || form.referencia_catastral || id.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={() => navigate(`/activos/${id}`)} className="btn-secondary" data-testid="cancel-btn">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-btn">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      </div>

      <div className="space-y-6">
        <Sec title="Información Básica">
          <G>
            <F label="Tipo Activo Operativo" help="Modalidad de adquisición o naturaleza del activo. Define qué motor de ROI se aplica y qué documentos son obligatorios.">
              <select value={form.tipo_activo_operativo || ''} onChange={e => set('tipo_activo_operativo', e.target.value || null)} className="input-base" data-testid="f-tipo-operativo">
                <option value="">Sin asignar</option><option value="CREDITO">Crédito</option><option value="SUBASTA">Subasta</option><option value="REMATE">Remate</option><option value="COMPRA_DIRECTA">Compra Directa</option><option value="OCUPADO">Ocupado</option>
              </select>
            </F>
            <F label="Proveedor" help="Origen del activo (fondo / servicer / entidad). Se usa para agrupar oportunidades y filtrar en Base de Activos."><input className="input-base" value={form.proveedor || ''} onChange={e => set('proveedor', e.target.value)} data-testid="f-proveedor" /></F>
            <F label="Referencia Catastral" help="Código catastral único de 20 caracteres. Sirve para conectar con la sede electrónica del Catastro y detectar duplicados."><input className="input-base" value={form.referencia_catastral || ''} onChange={e => set('referencia_catastral', e.target.value)} data-testid="f-ref-catastral" /></F>
            <F label="Dirección" help="Dirección postal completa (calle, número, planta, puerta)."><input className="input-base" value={form.direccion || ''} onChange={e => set('direccion', e.target.value)} data-testid="f-direccion" /></F>
            <F label="Municipio" help="Municipio donde se ubica el inmueble. Se usa para geolocalización y comparables de mercado."><input className="input-base" value={form.municipio || ''} onChange={e => set('municipio', e.target.value)} data-testid="f-municipio" /></F>
            <F label="Provincia" help="Provincia donde se ubica el inmueble. Alimenta filtros y estadísticas."><input className="input-base" value={form.provincia || ''} onChange={e => set('provincia', e.target.value)} data-testid="f-provincia" /></F>
            <F label="Latitud (WGS84)" help="Opcional. Grados decimales (-90 a 90). Las obtenidas de Catastro son el centroide de la parcela."><input type="number" step="any" min="-90" max="90" className="input-base" value={form.latitud ?? ''} onChange={e => setCoord('latitud', e.target.value)} data-testid="f-latitud" /></F>
            <F label="Longitud (WGS84)" help="Opcional. Grados decimales (-180 a 180). En España peninsular suele ser negativa al oeste de Greenwich."><input type="number" step="any" min="-180" max="180" className="input-base" value={form.longitud ?? ''} onChange={e => setCoord('longitud', e.target.value)} data-testid="f-longitud" /></F>
            <F label="Acceso visitas · Latitud" help="Opcional. Punto exacto de entrada para visitas (WGS84). Tiene prioridad sobre la ubicación catastral en los recorridos y nunca se sobrescribe al consultar Catastro."><input type="number" step="any" min="-90" max="90" className="input-base" value={form.acceso_latitud ?? ''} onChange={e => setNum('acceso_latitud', e.target.value)} data-testid="f-acceso-latitud" /></F>
            <F label="Acceso visitas · Longitud" help="Opcional. Longitud del punto de acceso (WGS84, negativa al oeste de Greenwich)."><input type="number" step="any" min="-180" max="180" className="input-base" value={form.acceso_longitud ?? ''} onChange={e => setNum('acceso_longitud', e.target.value)} data-testid="f-acceso-longitud" /></F>
            <F label="Acceso visitas · Indicaciones" help="Opcional. Ej.: portal trasero, acceso por camino, llaves en conserjería."><input className="input-base" value={form.acceso_descripcion || ''} onChange={e => set('acceso_descripcion', e.target.value)} data-testid="f-acceso-descripcion" /></F>
            <F label="Zona / Barrio" help="Sub-área del municipio para afinar la valoración por comparables."><input className="input-base" value={form.zona_barrio || ''} onChange={e => set('zona_barrio', e.target.value)} data-testid="f-zona-barrio" /></F>
            <F label="Tipo Inmueble" help="Tipología del bien (piso, vivienda unifamiliar, local, etc.). Determina la lógica de comparables Fotocasa."><input className="input-base" value={form.tipo_activo || ''} onChange={e => set('tipo_activo', e.target.value)} data-testid="f-tipo-activo" /></F>
            <F label="Metros (m²)" help="Superficie construida del inmueble usada para calcular precio/m² y comparar con la zona."><input type="number" className="input-base" value={form.metros ?? ''} onChange={e => setNum('metros', e.target.value)} data-testid="f-metros" /></F>
            <F label="Estado" help="Situación del activo en XÉNICOS: Activo, Standby, Cerrado o Papelera. Cerrado bloquea la edición de FASE.">
              <select value={form.estado || 'activo'} onChange={e => handleEstadoChange(e.target.value)} className="input-base" data-testid="f-estado" title="Situación de la oportunidad dentro de XÉNICOS">
                <option value="activo">Activo</option><option value="standby">Standby</option><option value="cerrado">Cerrado</option><option value="papelera">Papelera</option>
              </select>
            </F>
            <F label="Prioridad" help="Nivel de urgencia con el que el equipo debe atender el activo.">
              <select value={form.prioridad || 'normal'} onChange={e => set('prioridad', e.target.value)} className="input-base" data-testid="f-prioridad">
                <option value="baja">Baja</option><option value="normal">Normal</option><option value="alta">Alta</option><option value="urgente">Urgente</option><option value="no_prioritario">No prioritario</option>
              </select>
            </F>
            <F label="Fase" help="Fase del pipeline de adquisición: Nuevo, Estudio, Mediación, En Gestión, Oferta, Proceso compra o Adquirido.">
              <select value={form.etapa_visual || ''} onChange={e => set('etapa_visual', e.target.value || null)} className="input-base" data-testid="f-etapa-visual" title="Fase del pipeline de adquisición">
                <option value="">— sin fase —</option>
                <option value="nuevo">Nuevo</option>
                <option value="estudio">Estudio</option>
                <option value="mediacion">Mediación</option>
                <option value="en_gestion">En Gestión</option>
                <option value="oferta">Oferta</option>
                <option value="proceso_compra">Proceso compra</option>
                <option value="adquirido">Adquirido</option>
              </select>
              {/* (Feb 2026 mediacion_activa) Toggle manual: permite mantener
                  el activo contabilizado en la card Mediación aunque su fase
                  sea otra. No se resetea al cambiar de fase. */}
              <label
                className="flex items-center gap-2 mt-2 text-xs cursor-pointer text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5 hover:bg-emerald-100 font-medium"
                data-testid="mediacion-activa-wrapper"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={!!form.mediacion_activa}
                  onChange={e => set('mediacion_activa', e.target.checked)}
                  data-testid="f-mediacion-activa"
                />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                En mediación activa
              </label>
            </F>
            <F label="Asignado a" help="Responsable principal del activo (texto libre). Complementa la asignación por Gestores/Mediadores."><input className="input-base" value={form.asignado_a || ''} readOnly={!isAdminOrSuper} onChange={e => set('asignado_a', e.target.value)} data-testid="f-asignado" /></F>
          </G>

          {!isAdminOrSuper && <AssetAssignments asset={form} currentUser={user} canEdit={false} />}
          {isAdminOrSuper && (
            <>
              <h4 className="text-sm font-semibold text-slate-700 mt-6 mb-3">Gestores, Mediadores e Inversores</h4>
              <G>
                <F label="Gestores (pueden editar)" help="Usuarios asignados como GESTOR del activo. Pueden editar la ficha. La 'X' de cada chip los quita del activo.">
                  <UserChipsPicker
                    value={form.gestores || []}
                    options={users.filter(u => u.role !== 'inversor')}
                    onChange={(v) => set('gestores', v)}
                    placeholder="Añadir gestor…"
                    testId="f-gestores"
                  />
                </F>
                <F label="Mediadores asignados" help="Usuarios asignados como MEDIADOR del activo. Se les notifica en fase Mediación. La 'X' de cada chip los desasigna.">
                  <UserChipsPicker
                    value={form.mediadores || []}
                    options={users}
                    onChange={(v) => set('mediadores', v)}
                    placeholder="Añadir mediador…"
                    testId="f-mediadores"
                  />
                </F>
                <F label="Inversores (solo lectura)" help="Usuarios asignados como INVERSOR del activo (solo lectura si está publicado). La 'X' de cada chip los desasigna.">
                  <UserChipsPicker
                    value={form.inversores || []}
                    options={users}
                    onChange={(v) => set('inversores', v)}
                    placeholder="Añadir inversor…"
                    testId="f-inversores"
                  />
                </F>
              </G>
            </>
          )}
          {/* Checkbox opcional: al guardar con fase=Mediación, marca el
              activo como Solicitada Revisión y notifica a los admins
              asignados (spec Feb 2026). Solo aparece cuando la fase actual
              es "mediacion". */}
          {form.etapa_visual === 'mediacion' && (
            <label
              className="flex items-center gap-2 mt-4 text-xs cursor-pointer text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 hover:bg-amber-100 font-medium"
              data-testid="pendiente-mediador-wrapper"
            >
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={pendienteMediador}
                onChange={e => setPendienteMediador(e.target.checked)}
                data-testid="f-pendiente-mediador"
              />
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
              Pendiente de mediador — solicitar revisión a los admins asignados
            </label>
          )}
        </Sec>

        <Sec title="Datos Económicos">
          <G>
            <F label={`Precio Compra (€)${form.precio_compra_es_estimado ? ' *' : ''}`} help="Precio al que XÉNICOS adquiere el activo. Base para calcular ROI, escenarios y márgenes.">
              <input type="number" className="input-base" value={form.precio_compra ?? ''} onChange={e => setNum('precio_compra', e.target.value)} data-testid="f-precio_compra" />
              {/* Checkbox manual "En descuento" (spec Feb 2026). Se aplica al
                  guardar comparando `precio_compra` actual vs original. */}
              <label className="flex items-center gap-2 mt-2 text-xs cursor-pointer text-orange-800 bg-orange-50 border border-orange-200 rounded-md px-2 py-1.5 hover:bg-orange-100 font-medium" data-testid="manual-discount-wrapper">
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={manualDiscount}
                  onChange={e => setManualDiscount(e.target.checked)}
                  data-testid="f-manual-discount"
                />
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500" />
                Marcar activo como <b>En descuento</b>
                <span className="text-orange-600 font-normal">
                  {manualDiscount ? '· al guardar, el precio nuevo debe ser inferior al anterior' : ''}
                </span>
              </label>
              {form.precio_compra_es_estimado && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                  <p className="font-medium">⚠ Precio estimado automáticamente</p>
                  {form.descuento_deuda_pct != null && form.precio_compra_estimado != null && (
                    <p className="text-amber-700">
                      Calculado como Deuda × (1 − {form.descuento_deuda_pct}%) = {new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR'}).format(form.precio_compra_estimado)}
                    </p>
                  )}
                  <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="precio-confirmado-wrapper">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={false}
                      onChange={e => {
                        if (e.target.checked) set('precio_compra_es_estimado', false);
                      }}
                      data-testid="f-precio-confirmado"
                    />
                    <span className="font-medium text-amber-900">Precio confirmado (dejar de mostrar el asterisco *)</span>
                  </label>
                </div>
              )}
            </F>
            <F label={`Deuda Total (€)${(form.deudas || []).length > 1 ? ' · suma auto' : ''}`} help="Deuda pendiente sobre el activo. Cuando hay varias deudas se calcula automáticamente como la suma de los importes individuales.">
              <input
                type="number"
                className="input-base"
                value={form.deuda_total ?? ''}
                onChange={e => setNum('deuda_total', e.target.value)}
                disabled={(form.deudas || []).length > 1}
                title={(form.deudas || []).length > 1 ? 'Calculado como suma de las deudas individuales' : ''}
                data-testid="f-deuda_total"
              />
            </F>
            <F label="Tasación (€)" help="Valor de tasación oficial del inmueble (bancario o pericial)."><input type="number" className="input-base" value={form.tasacion ?? ''} onChange={e => setNum('tasacion', e.target.value)} data-testid="f-tasacion" /></F>
            <F label="Valor Adjudicación (€)" help="Precio de adjudicación en subasta o adjudicación judicial. Solo aplica a subastas / remates."><input type="number" className="input-base" value={form.valor_adjudicacion ?? ''} onChange={e => setNum('valor_adjudicacion', e.target.value)} data-testid="f-valor_adjudicacion" /></F>
            <F label="Valor Referencia (€)" help="Valor de referencia catastral (usado como base fiscal para impuestos de transmisión)."><input type="number" className="input-base" value={form.valor_referencia ?? ''} onChange={e => setNum('valor_referencia', e.target.value)} data-testid="f-valor_referencia" /></F>
            <F label="Valor Salida Estimado (€)" help="Estimación de venta del activo. Base para calcular la rentabilidad y el ROI rápido."><input type="number" className="input-base" value={form.valor_salida_base ?? ''} onChange={e => setNum('valor_salida_base', e.target.value)} data-testid="f-valor_salida_base" /></F>
            <F label="Tiempo Resolución (meses)" help="Plazo estimado hasta la venta final del activo. Se usa para anualizar el ROI."><input type="number" className="input-base" value={form.tiempo_resolucion_meses ?? ''} onChange={e => setNum('tiempo_resolucion_meses', e.target.value)} data-testid="f-tiempo_resolucion_meses" /></F>
            <F label="Fecha de Compra" help="Fecha efectiva de compra del activo por XÉNICOS."><input type="date" className="input-base" value={form.fecha_compra || ''} onChange={e => set('fecha_compra', e.target.value || null)} data-testid="f-fecha_compra" /></F>
            <F label="Fecha de Venta" help="Fecha estimada o real de venta del activo. Base para calcular el ROI anualizado real."><input type="date" className="input-base" value={form.fecha_venta || ''} onChange={e => set('fecha_venta', e.target.value || null)} data-testid="f-fecha_venta" /></F>
            <F label="Vivienda Habitual" help="Indica si el inmueble es vivienda habitual del deudor. Afecta al escenario fiscal por ley (ITP/IVA + 70% vs 50% de tasación).">
              <select
                className="input-base"
                value={form.vivienda_habitual || 'NO_CONSTA'}
                onChange={e => set('vivienda_habitual', e.target.value)}
                data-testid="f-vivienda"
              >
                <option value="NO_CONSTA">No consta (asume habitual · cálculo conservador 70%)</option>
                <option value="SI">Sí (habitual · 70% tasación)</option>
                <option value="NO">No (50% tasación)</option>
              </select>
            </F>
          </G>
        </Sec>

        <Sec title="Deudas que gravan el inmueble">
          <DeudasEditor deudas={form.deudas || []} setDeudas={(next) => setForm(prev => ({ ...prev, deudas: next }))} />
        </Sec>

        <Sec title="Participación en beneficios">
          <ParticipantesEditor
            participantes={form.participantes || []}
            setParticipantes={(next) => setForm(prev => ({ ...prev, participantes: next }))}
            users={users}
          />
        </Sec>

        <Sec title="Impuestos">
          <G>
            <F label="ITP (%)"><input type="number" step="0.01" className="input-base" value={form.tipo_impositivo_itp ?? ''} onChange={e => setNum('tipo_impositivo_itp', e.target.value)} data-testid="f-itp" /></F>
            <F label="AJD (%)"><input type="number" step="0.01" className="input-base" value={form.tipo_impositivo_ajd ?? ''} onChange={e => setNum('tipo_impositivo_ajd', e.target.value)} data-testid="f-ajd" /></F>
            <F label="IVA (%)"><input type="number" step="0.01" className="input-base" value={form.tipo_impositivo_iva ?? ''} onChange={e => setNum('tipo_impositivo_iva', e.target.value)} data-testid="f-iva" /></F>
            <F label="Coste Posesión (€)"><input type="number" className="input-base" value={form.coste_posesion ?? ''} onChange={e => setNum('coste_posesion', e.target.value)} data-testid="f-posesion" /></F>
          </G>
        </Sec>

        <Sec title={`Líneas de Gasto (Total: ${new Intl.NumberFormat('es-ES', {style:'currency',currency:'EUR'}).format(gastosTotal)})`}>
          <div className="flex gap-2 mb-3">
            <input className="input-base flex-1" value={newGasto.concepto} onChange={e => setNewGasto({...newGasto, concepto: e.target.value})} placeholder="Concepto" data-testid="g-concepto" />
            <input type="number" className="input-base w-28" value={newGasto.importe} onChange={e => setNewGasto({...newGasto, importe: e.target.value})} placeholder="Importe" data-testid="g-importe" />
            <select className="input-base w-36" value={newGasto.categoria} onChange={e => setNewGasto({...newGasto, categoria: e.target.value})} data-testid="g-categoria">
              <option value="impuestos">Impuestos</option><option value="adjudicacion">Adjudicación</option><option value="juridico">Jurídico</option><option value="registral">Registral</option><option value="posesion">Posesión</option><option value="reforma">Reforma</option><option value="comercializacion">Comercialización</option><option value="otros">Otros</option>
            </select>
            <button onClick={addGasto} disabled={!newGasto.concepto || !newGasto.importe} className="btn-primary px-3" data-testid="g-add-btn">+</button>
          </div>
          {gastos.map(g => (
            <div key={g.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded mb-1">
              <span className="flex-1 text-sm">{g.concepto}</span>
              <span className="badge-neutral text-xs">{g.categoria}</span>
              <span className="text-sm font-semibold w-24 text-right">{new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR'}).format(g.importe)}</span>
              <button onClick={() => deleteGasto(g.id)} className="text-red-500 hover:text-red-700 text-sm px-2" data-testid={`g-del-${g.id}`}>x</button>
            </div>
          ))}
        </Sec>

        <Sec title="Observaciones">
          <textarea className="input-base min-h-[80px] w-full" value={form.observaciones_importacion || ''} onChange={e => set('observaciones_importacion', e.target.value)} data-testid="f-observaciones" />
        </Sec>

        <div className="flex justify-end gap-3 pb-8">
          <button onClick={() => navigate(`/activos/${id}`)} className="btn-secondary" data-testid="cancel-btn-bottom">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-btn-bottom">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
        </div>
      </div>

      {/* Descarte motivos modal */}
      {showDescarte && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="descarte-modal">
          <div className="rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Motivo de cierre</h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Selecciona el motivo principal por el que se cierra este activo.</p>
              </div>
              <button onClick={() => setShowDescarte(false)} className="text-xl hover:opacity-80" style={{ color: 'var(--color-text-muted)' }} data-testid="descarte-close-btn">×</button>
            </div>
            <div className="space-y-2">
              {EDIT_DESCARTE_MOTIVOS.map(m => (
                <label key={m.v} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:opacity-90" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }} data-testid={`descarte-motivo-${m.v}`}>
                  <input
                    type="radio"
                    name="edit-descarte-motivo"
                    className="w-4 h-4"
                    checked={descarteDraft.includes(m.v)}
                    onChange={() => toggleDescarteDraft(m.v)}
                  />
                  <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{m.l}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowDescarte(false)} className="btn-secondary text-sm" data-testid="descarte-cancel-btn">Cancelar</button>
              <button onClick={confirmDescarte} disabled={!descarteDraft.length} className="btn-primary text-sm" data-testid="descarte-confirm-btn">Confirmar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Sec = ({ title, children }) => <div className="card-base p-6"><h2 className="heading-3 mb-4">{title}</h2>{children}</div>;
const G = ({ children }) => <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
const F = ({ label, help, children }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-600">
      {label}
      {help && <HelpHint text={help} />}
    </label>
    {children}
  </div>
);

// ---- Editor multi-deuda ----
const emptyDeuda = () => ({
  id: `new-${Math.random().toString(36).slice(2, 10)}`,
  deuda_total: null,
  deuda_reclamada: null,
  acreedor: '',
  tipo_gravamen: '',
  numero_prestamo: '',
  nif_deudor: '',
  fecha_prestamo: '',
  observaciones: '',
});

const DeudasEditor = ({ deudas, setDeudas }) => {
  const update = (idx, field, value) => {
    const next = deudas.map((d, i) => i === idx ? { ...d, [field]: value } : d);
    setDeudas(next);
  };
  const updateNum = (idx, field, raw) => update(idx, field, raw === '' ? null : parseFloat(raw));
  const remove = (idx) => {
    if (!window.confirm('¿Eliminar esta deuda?')) return;
    setDeudas(deudas.filter((_, i) => i !== idx));
  };
  const add = () => setDeudas([...(deudas || []), emptyDeuda()]);

  const totalDeuda = deudas.reduce((s, d) => s + (Number(d.deuda_total) || 0), 0);
  const totalReclamada = deudas.reduce((s, d) => s + (Number(d.deuda_reclamada) || 0), 0);
  const fmt = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

  return (
    <div className="space-y-4" data-testid="deudas-editor">
      {(!deudas || deudas.length === 0) ? (
        <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
          <p className="text-sm">Este activo no tiene deudas individualizadas.</p>
          <p className="text-xs mt-1">Usa el campo <em>Deuda Total</em> para una deuda única, o añade varias si el inmueble está gravado por más de un préstamo.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex gap-4 text-slate-600">
              <span>Total reclamada: <strong className="text-slate-900">{fmt(totalReclamada)}</strong></span>
              <span>Suma deuda total: <strong className="text-slate-900">{fmt(totalDeuda)}</strong></span>
            </div>
            <span className="text-xs text-slate-500">{deudas.length} deuda{deudas.length !== 1 ? 's' : ''}. Los campos arriba de "Deuda Total" se calculan como suma.</span>
          </div>
          {deudas.map((d, idx) => (
            <div key={d.id || idx} className="border border-slate-200 rounded-lg p-4 bg-slate-50/50" data-testid={`deuda-row-${idx}`}>
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 bg-slate-900 text-white text-xs font-semibold rounded-full inline-flex items-center justify-center">{idx + 1}</span>
                  <span className="text-sm font-medium text-slate-700">
                    {d.acreedor || 'Sin acreedor'} · {d.tipo_gravamen || 'Sin rango'}
                  </span>
                  {d.fecha_importacion && (
                    <span className="text-xs text-slate-400">importado {new Date(d.fecha_importacion).toLocaleDateString('es-ES')}</span>
                  )}
                </div>
                <button
                  onClick={() => remove(idx)}
                  className="text-red-500 hover:text-red-700 text-sm font-medium"
                  data-testid={`deuda-delete-${idx}`}
                >Eliminar</button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Acreedor</label>
                  <input className="input-base" value={d.acreedor || ''} onChange={e => update(idx, 'acreedor', e.target.value)} data-testid={`deuda-acreedor-${idx}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Tipo / Rango</label>
                  <select className="input-base" value={d.tipo_gravamen || ''} onChange={e => update(idx, 'tipo_gravamen', e.target.value || null)} data-testid={`deuda-tipo-${idx}`}>
                    <option value="">—</option>
                    <option value="1ª hipoteca">1ª hipoteca</option>
                    <option value="2ª hipoteca">2ª hipoteca</option>
                    <option value="3ª hipoteca">3ª hipoteca</option>
                    <option value="Embargo">Embargo</option>
                    <option value="Anotación preventiva">Anotación preventiva</option>
                    <option value="Hacienda">Hacienda</option>
                    <option value="Seguridad Social">Seguridad Social</option>
                    <option value="Comunidad">Comunidad</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Nº Préstamo / Expediente</label>
                  <input className="input-base" value={d.numero_prestamo || ''} onChange={e => update(idx, 'numero_prestamo', e.target.value)} data-testid={`deuda-num-${idx}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Deuda Total (€)</label>
                  <input type="number" className="input-base" value={d.deuda_total ?? ''} onChange={e => updateNum(idx, 'deuda_total', e.target.value)} data-testid={`deuda-total-${idx}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Deuda Reclamada (€)</label>
                  <input type="number" className="input-base" value={d.deuda_reclamada ?? ''} onChange={e => updateNum(idx, 'deuda_reclamada', e.target.value)} data-testid={`deuda-reclamada-${idx}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">NIF deudor</label>
                  <input className="input-base" value={d.nif_deudor || ''} onChange={e => update(idx, 'nif_deudor', e.target.value)} data-testid={`deuda-nif-${idx}`} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-600">Fecha préstamo</label>
                  <input type="date" className="input-base" value={d.fecha_prestamo || ''} onChange={e => update(idx, 'fecha_prestamo', e.target.value || null)} data-testid={`deuda-fecha-${idx}`} />
                </div>
                <div className="space-y-1 lg:col-span-3">
                  <label className="text-xs text-slate-600">Observaciones</label>
                  <input className="input-base" value={d.observaciones || ''} onChange={e => update(idx, 'observaciones', e.target.value)} data-testid={`deuda-obs-${idx}`} placeholder="Notas internas sobre este gravamen..." />
                </div>
              </div>
            </div>
          ))}
        </>
      )}
      <button
        type="button"
        onClick={add}
        className="btn-secondary text-sm inline-flex items-center gap-2"
        data-testid="deuda-add-btn"
      >+ Añadir deuda</button>
    </div>
  );
};

export default EditAssetPage;

// ---- Editor participantes (gestores/inversores con aportación) ----
const emptyParticipante = () => ({
  id: `new-${Math.random().toString(36).slice(2, 10)}`,
  user_id: null,
  nombre: '',
  rol: 'inversor',
  aportacion: null,
  observaciones: '',
});

const ParticipantesEditor = ({ participantes, setParticipantes, users }) => {
  const update = (idx, field, value) => {
    const next = participantes.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    setParticipantes(next);
  };
  const updateUserId = (idx, userId) => {
    const next = participantes.map((p, i) => {
      if (i !== idx) return p;
      if (!userId) return { ...p, user_id: null };
      const u = users.find(x => x.id === userId);
      return { ...p, user_id: userId, nombre: u ? (u.name || u.email) : p.nombre };
    });
    setParticipantes(next);
  };
  const updateNum = (idx, field, raw) => update(idx, field, raw === '' ? null : parseFloat(raw));
  const remove = (idx) => {
    if (!window.confirm('¿Eliminar esta línea?')) return;
    setParticipantes(participantes.filter((_, i) => i !== idx));
  };
  const add = () => setParticipantes([...(participantes || []), emptyParticipante()]);

  const totalAport = participantes.reduce((s, p) => s + (Number(p.aportacion) || 0), 0);
  const fmt = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v || 0);

  return (
    <div className="space-y-3" data-testid="participantes-editor">
      <p className="text-sm text-slate-600">
        Añade gestores e inversores con su aportación en euros. El <strong>% de participación</strong> se calcula automáticamente como su aportación sobre la suma total. Usa "Externo / Anónimo" para participantes no registrados.
      </p>
      {(!participantes || participantes.length === 0) ? (
        <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
          <p className="text-sm">Sin participantes registrados en la rentabilidad.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-slate-600">Aportación total: <strong className="text-slate-900">{fmt(totalAport)}</strong></span>
            <span className="text-xs text-slate-500">{participantes.length} participante{participantes.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 w-8">#</th>
                  <th className="py-2 pr-3">Rol</th>
                  <th className="py-2 pr-3">Usuario</th>
                  <th className="py-2 pr-3">Nombre (si externo)</th>
                  <th className="py-2 pr-3 text-right">Aportación (€)</th>
                  <th className="py-2 pr-3 text-right">%</th>
                  <th className="py-2 pr-3">Observaciones</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {participantes.map((p, idx) => {
                  const pct = totalAport > 0 ? ((Number(p.aportacion) || 0) / totalAport * 100) : 0;
                  return (
                    <tr key={p.id || idx} className="border-b border-slate-100" data-testid={`participante-row-${idx}`}>
                      <td className="py-2 pr-3 text-slate-500">{idx + 1}</td>
                      <td className="py-2 pr-3">
                        <select value={p.rol || ''} onChange={e => update(idx, 'rol', e.target.value)} className="input-base text-xs" data-testid={`part-rol-${idx}`}>
                          <option value="gestor">Gestor</option>
                          <option value="inversor">Inversor</option>
                          <option value="anonimo">Otro</option>
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <select value={p.user_id || ''} onChange={e => updateUserId(idx, e.target.value || null)} className="input-base text-xs min-w-[160px]" data-testid={`part-user-${idx}`}>
                          <option value="">— Externo / Anónimo —</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <input
                          className="input-base text-xs min-w-[140px]"
                          value={p.nombre || ''}
                          onChange={e => update(idx, 'nombre', e.target.value)}
                          placeholder={p.user_id ? '(del usuario)' : 'Nombre visible'}
                          data-testid={`part-nombre-${idx}`}
                        />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <input type="number" className="input-base text-xs text-right w-28" value={p.aportacion ?? ''} onChange={e => updateNum(idx, 'aportacion', e.target.value)} data-testid={`part-aport-${idx}`} />
                      </td>
                      <td className="py-2 pr-3 text-right font-medium tabular-nums">{pct.toFixed(2)}%</td>
                      <td className="py-2 pr-3">
                        <input className="input-base text-xs min-w-[140px]" value={p.observaciones || ''} onChange={e => update(idx, 'observaciones', e.target.value)} data-testid={`part-obs-${idx}`} />
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <button onClick={() => remove(idx)} className="text-red-500 hover:text-red-700 text-xs font-medium" data-testid={`part-delete-${idx}`}>Eliminar</button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-semibold">
                  <td className="py-2 pr-3" colSpan={4}>TOTAL</td>
                  <td className="py-2 pr-3 text-right">{fmt(totalAport)}</td>
                  <td className="py-2 pr-3 text-right">{totalAport > 0 ? '100,00%' : '—'}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
      <button type="button" onClick={add} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="part-add-btn">+ Añadir participante</button>
    </div>
  );
};
